import React, { useState } from 'react';
import QrDniReader from './QrDniReader';
import { db } from '../../firebase/firebase.config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Container, Modal, FormGroup } from 'react-bootstrap';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import AutoexcluidosDetalle from './AutoexcluidosDetalle';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../../context/AuthContext';
import { logConsulta } from '../../helpers/logConsulta';

function AutoexcluidosLookup() {
  const auth = useAuth();

  // Devuelve el objeto authUser usado por logConsulta
  const authUserParaLog = () => ({
    uid: auth.id,
    email: auth.user,
    rol: auth.role,
    casino: auth.casino,
  });

  // Registra una acción. Si falla, alerta y devuelve false (caller debe abortar).
  async function registrar(tipoAccion, valorBusqueda, resultado) {
    try {
      await logConsulta({ authUser: authUserParaLog(), tipoAccion, valorBusqueda, resultado });
      return true;
    } catch (e) {
      console.error('[Autoexcluidos] No se pudo registrar la consulta:', e);
      alert('No se pudo registrar la consulta en el sistema. Verificá tu conexión y volvé a intentar.');
      return false;
    }
  }
  const [showQr, setShowQr] = useState(false);
  const [searchType, setSearchType] = useState('dni');
  const [searchValue, setSearchValue] = useState('');
  const [autoexcluidos, setAutoexcluidos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedAutoexcluido, setSelectedAutoexcluido] = useState(null);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buscarActivos, setBuscarActivos] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [qrFoundDni, setQrFoundDni] = useState(null);  // DNI leído por QR, mientras consulta BD
  const [lastSearched, setLastSearched] = useState({ dni: '', nombre: '' }); // para mensaje "no encontrado"

  const columns = [
    { field: 'nombre', headerName: 'Nombre', width: 130 },
    { field: 'fechaNacimiento', headerName: 'Fecha de Nacimiento', width: 180 },
    { field: 'vencimiento', headerName: 'Vencimiento', width: 180 },
    { field: 'dni', headerName: 'DNI', width: 130 },
    { field: 'telefono', headerName: 'Teléfono', width: 130 },
    {
      field: 'action',
      headerName: 'Detalle',
      width: 170,
      renderCell: (params) => (
        <button
          style={{
            background: params.row.empleado ? '#43a047' : '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '4px 12px',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: 15
          }}
          onClick={async () => {
            const ok = await registrar('ver-detalle', params.row.dni || params.row.nombre || '', {
              encontrado: true,
              tipo: params.row.empleado ? 'empleado' : 'autoexcluido',
              cantidad: 1,
              dniEncontrado: params.row.dni ? String(params.row.dni) : null,
            });
            if (!ok) return;
            if (params.row.empleado) {
              setSelectedEmpleado(params.row);
              setSelectedAutoexcluido(null);
            } else {
              setSelectedAutoexcluido(params.row);
              setSelectedEmpleado(null);
            }
            setShowModal(true);
          }}
        >
          Ver Detalle
        </button>
      ),
    },
  ];

  // Ya no se usa handleRowClick, la lógica está en el botón de la columna

  const handleSearchTypeChange = (event) => {
    setSearchType(event.target.value);
    setSearchValue('');
  };

  const handleSearchInputChange = (event) => {
    const inputValue = event.target.value;
    if ((searchType === 'dni' && /^\d*$/.test(inputValue)) || (searchType === 'nombre' && /^[a-zA-Z\s]*$/.test(inputValue))) {
      setSearchValue(inputValue);
    }
  };

  async function buscarPorDni(dni, nombreCompleto = '', tipoAccion = 'buscar-dni') {
    setNotFound(false);
    setLastSearched({ dni, nombre: nombreCompleto });
    setLoading(true);
    console.log('[Autoexcluidos] Buscando DNI:', dni);

    let resultadoLog = { encontrado: false, tipo: 'ninguno', cantidad: 0, dniEncontrado: null };
    let outcome = null; // { tipo: 'autoexcluido'|'empleado'|'notfound', data? }

    try {
      const dniStr = String(dni);

      // Buscar en autoexcluidos por DNI, filtrar activo client-side
      const autoSnap = await getDocs(
        query(collection(db, 'autoexcluidos'), where('dni', '==', dniStr))
      );
      const autoDoc = autoSnap.docs.find(d => d.data().activo === buscarActivos);
      if (autoDoc) {
        const data = { id: autoDoc.id, empleado: false, ...autoDoc.data() };
        outcome = { tipo: 'autoexcluido', data };
        resultadoLog = { encontrado: true, tipo: 'autoexcluido', cantidad: 1, dniEncontrado: dniStr };
      } else {
        // Buscar en empleados (DNI puede ser número o string en Firestore)
        const dniNum = Number(dniStr);
        let empSnap = !isNaN(dniNum)
          ? await getDocs(query(collection(db, 'empleados'), where('dni', '==', dniNum)))
          : { empty: true, docs: [] };
        if (empSnap.empty) {
          empSnap = await getDocs(query(collection(db, 'empleados'), where('dni', '==', dniStr)));
        }
        if (!empSnap.empty) {
          const empData = empSnap.docs[0].data();
          const nombre = `${empData.apellido}, ${empData.nombre}`;
          outcome = { tipo: 'empleado', data: { nombre, dni: empData.dni, cargo: empData.cargo, foto: empData.foto } };
          resultadoLog = { encontrado: true, tipo: 'empleado', cantidad: 1, dniEncontrado: String(empData.dni) };
        } else {
          outcome = { tipo: 'notfound' };
        }
      }

      // Registrar la consulta ANTES de mostrar el resultado. Si falla, abortar.
      const ok = await registrar(tipoAccion, dniStr, resultadoLog);
      if (!ok) return;

      // Aplicar el resultado en la UI
      if (outcome.tipo === 'autoexcluido') {
        console.log('[Autoexcluidos] Encontrado:', outcome.data);
        setSelectedAutoexcluido(outcome.data);
        setSelectedEmpleado(null);
        setShowModal(true);
      } else if (outcome.tipo === 'empleado') {
        console.log('[Autoexcluidos] Empleado:', outcome.data.nombre);
        setSelectedEmpleado(outcome.data);
        setSelectedAutoexcluido(null);
        setShowModal(true);
      } else {
        console.log('[Autoexcluidos] No encontrado');
        setNotFound(true);
        setAutoexcluidos([]);
      }
    } catch (error) {
      console.error('[Autoexcluidos] Error:', error?.code, error?.message);
      setAutoexcluidos([]);
    } finally {
      setLoading(false);
    }
  }

  const handleSearch = async (event) => {
    event.preventDefault();
    setNotFound(false);
    if (searchType === 'dni') {
      if (!/^\d{7,8}$/.test(searchValue)) {
        alert('El DNI debe tener 7 u 8 dígitos numéricos.');
        return;
      }
      await buscarPorDni(searchValue, '');
      return;
    }
    try {
      setLoading(true);
      const nombreBusqueda = searchValue.toLowerCase();
      console.log('[Autoexcluidos] Buscando por nombre:', searchValue);

      const [autoSnap, empSnap] = await Promise.all([
        getDocs(collection(db, 'autoexcluidos')),
        getDocs(collection(db, 'empleados')),
      ]);

      const autoResults = [];
      autoSnap.forEach(doc => {
        const data = doc.data();
        const pasaFiltro = buscarActivos ? data.activo === true : data.activo === false;
        if (pasaFiltro && (data.nombre || '').toLowerCase().includes(nombreBusqueda)) {
          autoResults.push({ id: doc.id, empleado: false, ...data });
        }
      });

      const empResults = [];
      empSnap.forEach(doc => {
        const data = doc.data();
        if (
          (data.nombre || '').toLowerCase().includes(nombreBusqueda) ||
          (data.apellido || '').toLowerCase().includes(nombreBusqueda)
        ) {
          const nombre = `${data.apellido}, ${data.nombre}`;
          const { nombre: _, apellido: __, ...rest } = data;
          empResults.push({ id: doc.id, empleado: true, nombre, ...rest });
        }
      });

      const resultados = [...autoResults, ...empResults];
      console.log('[Autoexcluidos] Resultados:', resultados.length);

      // Registrar antes de mostrar. Si falla, no exponer datos.
      const ok = await registrar('buscar-nombre', searchValue, {
        encontrado: resultados.length > 0,
        tipo: resultados.length > 0 ? 'busqueda-multiple' : 'ninguno',
        cantidad: resultados.length,
        dniEncontrado: null,
      });
      if (!ok) return;

      setAutoexcluidos(resultados);
    } catch (error) {
      console.error('[Autoexcluidos] Error búsqueda nombre:', error);
      setAutoexcluidos([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-5 ">
      <Container style={{ height: '100%', width: '100%' }}>
        <div style={{ height: '100%', width: '100%' }}>
          <form onSubmit={handleSearch}>
            <div
              className="radio-buttons-container"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                alignItems: 'center',
                justifyContent: 'flex-start',
                marginBottom: 12,
                maxWidth: 600,
              }}
            >
              <div className="radio-button" style={{ minWidth: 120 }}>
                <input name="radio-group" id="radio2" className="radio-button__input" type="radio" value="dni" checked={searchType === 'dni'} onChange={handleSearchTypeChange} />
                <label htmlFor="radio2" className="radio-button__label">
                  <span className="radio-button__custom"></span>
                  Buscar por DNI
                </label>
              </div>
              <div className="radio-button" style={{ minWidth: 140 }}>
                <input name="radio-group" id="radio1" className="radio-button__input" type="radio" value="nombre" checked={searchType === 'nombre'} onChange={handleSearchTypeChange} />
                <label htmlFor="radio1" className="radio-button__label">
                  <span className="radio-button__custom"></span>
                  Buscar por nombre
                </label>
              </div>
              <input
                type={searchType === 'dni' ? 'number' : 'text'}
                value={searchValue}
                onChange={handleSearchInputChange}
                disabled={loading}
                style={{
                  flex: 1,
                  minWidth: 120,
                  maxWidth: 180,
                  marginRight: 4,
                  padding: 6,
                  borderRadius: 6,
                  border: '1px solid #ccc',
                  fontSize: 16,
                }}
                placeholder={searchType === 'dni' ? 'DNI' : 'Nombre'}
              />
            </div>
            {/* Fila 1: switch activos + botón buscar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <FormGroup style={{ marginBottom: 0 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={buscarActivos}
                      onChange={(e) => setBuscarActivos(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={buscarActivos ? "Buscando: Solo Activos" : "Buscando: Solo Inactivos"}
                />
              </FormGroup>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '6px 16px',
                  fontSize: 15,
                  borderRadius: 6,
                  background: '#1976d2',
                  color: '#fff',
                  border: 'none',
                  minWidth: 100,
                }}
              >
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
            {/* Fila 2: botón escanear (siempre debajo en mobile y desktop) */}
            <div style={{ marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => setShowQr(true)}
                title="Escanear QR del DNI"
                style={{
                  padding: '7px 18px',
                  fontSize: 15,
                  borderRadius: 6,
                  background: 'rgb(59,160,168)',
                  color: '#fff',
                  border: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  cursor: 'pointer',
                }}
              >
                <i className="pi pi-camera" /> Escanear DNI
              </button>
            </div>
          </form>
          <Modal
            show={showQr}
            onHide={() => { setShowQr(false); setQrFoundDni(null); }}
            size="md"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Escanear DNI</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {qrFoundDni ? (
                /* Pantalla de espera mientras consulta la BD */
                <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                  <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#2e7d32', marginBottom: 6 }}>
                    DNI {qrFoundDni} leído
                  </div>
                  <div style={{ color: '#1976d2', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <i className="pi pi-spin pi-spinner" />
                    Consultando base de datos...
                  </div>
                </div>
              ) : (
                <QrDniReader
                  onResult={async (data) => {
                    console.log('[QR] Datos leídos del DNI:', data);
                    if (!data.dni) { setShowQr(false); return; }
                    setSearchType('dni');
                    setSearchValue(data.dni);
                    setQrFoundDni(data.dni);
                    const nombreCompleto = [data.apellido, data.nombre].filter(Boolean).join(', ');
                    await buscarPorDni(data.dni, nombreCompleto, 'escanear-qr');
                    setShowQr(false);
                    setQrFoundDni(null);
                  }}
                  onClose={() => { setShowQr(false); setQrFoundDni(null); }}
                />
              )}
            </Modal.Body>
          </Modal>
          {notFound && (
            <div style={{
              margin: '16px 0',
              padding: '16px 20px',
              background: '#e8f5e9',
              border: '1.5px solid #43a047',
              borderRadius: 10,
              color: '#1b5e20',
              fontSize: 15,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
            }}>
              <i className="pi pi-check-circle" style={{ fontSize: 24, color: '#43a047', flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>
                  Puede ingresar
                </div>
                <div style={{ fontWeight: 400 }}>
                  El DNI <b>{lastSearched.dni}</b>
                  {lastSearched.nombre ? <> (<b>{lastSearched.nombre}</b>)</> : null}
                  {' '}no figura en la base de datos de autoexcluidos.
                </div>
              </div>
            </div>
          )}
          <br />
          {selectedEmpleado && (
            <Modal show={showModal} onHide={() => setShowModal(false)} size="md" centered>
              <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                <Modal.Title style={{ fontWeight: 700, fontSize: 22, color: '#1976d2', letterSpacing: 1 }}>Empleado INPROJUY</Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ padding: 28, paddingTop: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontSize: 38, color: '#1976d2', marginBottom: 8, width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {selectedEmpleado.foto ? (
                      <img
                        src={selectedEmpleado.foto}
                        alt="Foto del empleado"
                        style={{
                          width: 'min(38vw, 160px)',
                          height: 'min(38vw, 160px)',
                          maxWidth: 180,
                          maxHeight: 180,
                          minWidth: 90,
                          minHeight: 90,
                          objectFit: 'cover',
                          borderRadius: '50%',
                          border: '2.5px solid #1976d2',
                          background: '#fff',
                          marginBottom: 4,
                          boxShadow: '0 2px 12px rgba(25,118,210,0.10)'
                        }}
                      />
                    ) : (
                      <span role="img" aria-label="Empleado">👤</span>
                    )}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 20, color: '#222', textAlign: 'center', marginBottom: 2 }}>
                    {selectedEmpleado.nombre}
                  </div>
                  <div style={{ fontSize: 16, color: '#444', marginBottom: 2 }}>
                    <span style={{ fontWeight: 500, color: '#888' }}>DNI:</span> {selectedEmpleado.dni}
                  </div>
                  <div style={{ fontSize: 16, color: '#444', marginBottom: 10 }}>
                    <span style={{ fontWeight: 500, color: '#888' }}>Cargo:</span> <span style={{ color: '#1976d2', fontWeight: 600 }}>{selectedEmpleado.cargo}</span>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      background: '#1976d2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 17,
                      padding: '10px 32px',
                      marginTop: 10,
                      boxShadow: '0 2px 8px rgba(25,118,210,0.08)',
                      fontWeight: 600,
                      letterSpacing: 0.5
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              </Modal.Body>
            </Modal>
          )}
          {selectedAutoexcluido && (
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>Detalle del Autoexcluido</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <AutoexcluidosDetalle
                  autoexcluidoData={selectedAutoexcluido}
                  handleModalClose={() => setShowModal(false)}
                />
              </Modal.Body>
            </Modal>
          )}
          {/* Mostrar DataGrid para ambos tipos de búsqueda */}
          {(searchType === 'nombre' || searchType === 'dni') && Array.isArray(autoexcluidos) && autoexcluidos.length > 0 && (
            <div style={{ width: '100%', minHeight: 320, maxWidth: '100vw', overflowX: 'auto' }}>
              <DataGrid
                rows={autoexcluidos}
                columns={columns}
                pageSize={5}
                loading={loading}
                checkboxSelection={false}
                autoHeight
                getRowId={(row) => row.id ?? row.dni ?? Math.random().toString(36).substr(2, 9)}
                sx={{
                  '& .MuiDataGrid-root': { fontSize: { xs: 13, sm: 15 } },
                  '& .MuiDataGrid-columnHeaders': { fontSize: { xs: 13, sm: 15 } },
                  '& .MuiDataGrid-row.empleado-row': {
                    backgroundColor: '#e8f5e9 !important',
                  },
                }}
                getRowClassName={(params) => params.row.empleado ? 'empleado-row' : ''}
              />
            </div>
          )}
        </div>
      </Container>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalle del Autoexcluido</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAutoexcluido && (
            <AutoexcluidosDetalle
              autoexcluidoData={selectedAutoexcluido}
              handleModalClose={() => setShowModal(false)}
            />
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
}

export default AutoexcluidosLookup;
